import React, { useEffect, useMemo, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { Button } from 'shared-components';
import LoginService, { GetClientsResponse, LoginClientSummary } from '../../Services/LoginService';
import { LoginCard } from './LoginCard';
import Loading from '../../Abstracts/Components/Loading';

export const ClientChooser = () => {
	const history = useHistory();
	const [clients, setClients] = useState<LoginClientSummary[]>([]);
	const [errorCopy, setErrorCopy] = useState<string | null>(null);
	const [submittingId, setSubmittingId] = useState<number | null>(null);
	const [phase, setPhase] = useState<'loading' | 'choose' | 'redirecting' | 'empty' | 'error'>('loading');

	useEffect(() => {
		const loadClients = async () => {
			setErrorCopy(null);
			setPhase('loading');
			try {
				const response: GetClientsResponse = await LoginService.getClients();
				if ('error' in response) {
					if (response.code === 'not_authenticated') {
						history.replace('/');
						return;
					}
					if (response.code === 'rate_limited') {
						setErrorCopy('Too many attempts. Please wait a moment before trying again.');
						setPhase('error');
						return;
					}
					setErrorCopy('Unable to load your accounts right now.');
					setPhase('error');
					return;
				}
				const fetchedClients = response.clients ?? [];
				setClients(fetchedClients);

				if (fetchedClients.length === 0) {
					setPhase('empty');
					return;
				}

				if (fetchedClients.length === 1) {
					const client = fetchedClients[0];
					setSubmittingId(client!.id);
					setPhase('redirecting');
					window.location.assign(client!.redirect);
					return;
				}

				setPhase('choose');
			} catch (e) {
				console.error(e);
				setErrorCopy('Unable to load your accounts right now.');
				setPhase('error');
			}
		};
		void loadClients();
	}, [history]);

	const sortedClients = useMemo(() => {
		return [...clients].sort((a, b) => a.name.localeCompare(b.name));
	}, [clients]);

	const selectClient = (clientId: number) => {
		const client = clients.find((c) => c.id === clientId);
		if (!client) {
			return;
		}
		setSubmittingId(clientId);
		setPhase('redirecting');
		window.location.assign(client.redirect);
	};

	return (
		<LoginCard>
			{phase === 'loading' || phase === 'redirecting' ? (
				<Loading message={phase === 'redirecting' ? 'Redirecting' : 'Loading accounts'} />
			) : (
				<>
					<h1 className="login-title">Choose an account</h1>
					<p className="login-description">Select which stallholder account you&apos;d like to manage.</p>
					{errorCopy ? (
						<p className="login-error" role="alert">
							{errorCopy}
						</p>
					) : null}
					{!errorCopy && phase === 'choose' ? (
						<ul className="login-client-list">
							{sortedClients.map((client) => (
								<li key={client.id} className="login-client-item">
									<div className="login-client-details">
										<p className="login-client-name">{client.name}</p>
										{client.markets?.length ? (
											<p className="login-client-markets">
												{client.markets.slice(0, 3).join(', ')}
											</p>
										) : null}
									</div>
									<Button
										className="login-button login-client-button"
										color="primary"
										size="sm"
										variant="contained"
										onClick={() => selectClient(client.id)}
										disabled={submittingId === client.id}
									>
										{submittingId === client.id ? 'Loading...' : 'Continue'}
									</Button>
								</li>
							))}
						</ul>
					) : null}
					{!errorCopy && phase === 'empty' ? (
						<p className="login-description" role="status">
							No accounts are available for this login.
						</p>
					) : null}
					<div className="login-footer">
						<Link to="/" className="login-link">
							Use a different email
						</Link>
					</div>
				</>
			)}
		</LoginCard>
	);
};
